import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { CheckIcon, WarningOctagonIcon, XIcon } from "@phosphor-icons/react";

interface ToastItem {
  id: number;
  text: string;
  kind: "ok" | "error";
}

interface ToastApi {
  show: (text: string, kind?: "ok" | "error") => void;
}

const Ctx = createContext<ToastApi | null>(null);
const LIFETIME_MS = 4500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);

  const show = useCallback(
    (text: string, kind: "ok" | "error" = "ok") => {
      const id = next.current++;
      setItems((xs) => [...xs.slice(-2), { id, text, kind }]);
      setTimeout(() => dismiss(id), kind === "error" ? LIFETIME_MS * 2 : LIFETIME_MS);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ show }), [show]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {items.map((t) => (
          <div key={t.id} className={`toast${t.kind === "error" ? " toast--error" : ""}`} role={t.kind === "error" ? "alert" : "status"}>
            {t.kind === "error" ? (
              <WarningOctagonIcon size={18} aria-hidden="true" className="red-text" />
            ) : (
              <CheckIcon size={18} aria-hidden="true" />
            )}
            <p>{t.text}</p>
            <button type="button" className="btn btn--ghost btn--icon" aria-label="Dismiss message" onClick={() => dismiss(t.id)}>
              <XIcon size={14} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useToast outside ToastProvider");
  return v;
}
